<template>
    <!--
        AI products go two-column from `md`, where the panel moves out of the phone bottom sheet and
        beside the gallery. Regular products keep their existing `lg` switch — nothing about their
        layout changes here.
    -->
    <div
        data-test-id="pdp-gallery"
        class="relative flex w-full min-w-0 gap-2 lg:sticky lg:top-4 lg:h-fit lg:w-3/5"
        :class="{ 'md:w-3/5': productStore.isAiProduct }"
    >
        <!-- Product image label shown in the top right corner, only as fallback when the CP has no labels -->
        <!-- It sits under the heart and share buttons, which take the corner. -->
        <div
            v-if="productPageStore.configurableProduct.product_image_label && productPageStore.configurableProduct.labels.length === 0"
            class="absolute top-14 right-0 z-20 max-w-[90%] rounded-l-full bg-prices px-6 py-2 text-center font-bold text-wrap break-all lg:top-20 lg:max-w-75"
        >
            <span class="text-white" v-text="productPageStore.configurableProduct.product_image_label" />
        </div>

        <ProductMobileImageGallery
            v-if="previewImage"
            :images="images"
            :preview-image="previewImage"
            :is-ai-product="productStore.isAiProduct"
            class="lg:hidden"
        >
            <!-- The heart and share buttons sit on the image, or beside the dots. -->
            <template #default="{ besideDots }">
                <ProductGalleryActions
                    v-if="!productStore.isAiProduct"
                    :beside-dots="besideDots"
                    :class="{ 'absolute top-2 right-2 z-20': !besideDots }"
                />
            </template>
        </ProductMobileImageGallery>
        <ProductDesktopImageGallery
            v-if="previewImage"
            :images="images"
            :preview-image="previewImage"
            :is-ai-product="productStore.isAiProduct"
            class="hidden w-full lg:flex"
            @open-lightbox="openLightbox"
        >
            <ProductGalleryActions v-if="!productStore.isAiProduct" class="absolute -top-2 -right-2 z-20" />
        </ProductDesktopImageGallery>

        <ImageLightbox v-model:open="lightboxOpen" v-model:index="lightboxIndex" :images="lightboxImages" :alt-text="lightboxAltText" />
    </div>
</template>

<script lang="ts" setup>
import ImageLightbox, { type LightboxImage } from '@/components/gallery/ImageLightbox.vue';
import ProductDesktopImageGallery from '@/pages/product-page/partials/product-image-gallery/partials/ProductDesktopImageGallery.vue';
import ProductGalleryActions from '@/pages/product-page/partials/product-image-gallery/partials/ProductGalleryActions.vue';
import ProductMobileImageGallery from '@/pages/product-page/partials/product-image-gallery/partials/ProductMobileImageGallery.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { useProductStore } from '@/stores/products/ProductStore';
import { productImageAltText } from '@/utils/productImageAlt';
import { computed, ref } from 'vue';

const productPageStore = useProductPageStore();
const productStore = useProductStore();

const images = computed(() => productStore.selectedProductImages);
const previewImage = computed(() => images.value[0] ?? null);

const lightboxOpen = ref(false);
const lightboxIndex = ref(0);

const lightboxImages = computed((): LightboxImage[] =>
    images.value.map((image: LightboxImage): LightboxImage => ({ ...image, url: image.url ?? null })),
);

function lightboxAltText(image: LightboxImage, slide: number): string {
    return productImageAltText(productPageStore.configurableProduct.name, slide);
}

function openLightbox(index: number): void {
    lightboxIndex.value = index;
    lightboxOpen.value = true;
}
</script>
